import { Skeleton, SxProps, Typography } from "@mui/material";
import React, { memo } from "react";
import { Box, Icon } from "..";
import { createStyles, gradient, palette } from "../../../styles/theme";
import { useGlobalLoading } from "../../../utils/ContextHooks";
import {
  getConditionnalStyle,
  useThemeBreakpoints,
} from "../../../utils/themeUtils";
import { MaterialSymbolType } from "../Icon/IconConstant";

const styles = createStyles({
  container: {
    p: { xs: "16px", lg: "24px 36px 24px 24px" },
    borderRadius: "12px",
    border: `1px solid ${palette.grey.midGrey}`,
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    minHeight: { xs: "66px", lg: "72px" },
    maxHeight: { xs: "66px", lg: "72px" },
    cursor: "pointer",
    boxShadow: "2px 2px 4px 0px rgba(236, 236, 236, 0.40)",
    flex: { xs: "1 1 50%", sm: "unset" },
    "&:hover": {
      background: `${gradient.backgroundGradient},
        ${palette.borderGradientHover}`,
      border: "1px solid transparent",
    },
    "&:active": {
      // minus one on the padding to prevent content shifting cause be the increase of 1 px of border
      p: { xs: "15px", lg: "23px 35px 23px 23px" },
      background: palette.borderGradient,
      border: "2px solid transparent",
      ":hover": {
        background: `${gradient.backgroundGradient},
        ${palette.borderGradient}`,
        border: "2px solid transparent",
      },
    },
  },
  skeleton: {
    display: "flex",
    flex: { xs: "1 1 50%", sm: "unset" },
    width: {
      xs: 150,
      lg: 250,
    },
    p: { xs: "16px", lg: "24px 36px 24px 24px" },
    minHeight: { xs: "66px", lg: "100px" },
  },
  disabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
});

interface Props {
  text: string;
  subtext?: string;
  icon: MaterialSymbolType;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  isLoading?: boolean;
  disabled?: boolean;
  textSx?: SxProps;
}

const GradientButton = (props: Props) => {
  const { text, subtext, icon, onClick, isLoading, disabled, textSx, ...rest } =
    props;
  const { isGloballyLoading } = useGlobalLoading();
  const { isMobile } = useThemeBreakpoints();

  return isGloballyLoading || isLoading ? (
    <Skeleton variant="rounded" sx={styles.skeleton} />
  ) : (
    <Box
      sx={[styles.container, getConditionnalStyle(disabled, styles.disabled)]}
      onClick={onClick}
      {...rest}
    >
      <Icon
        icon={icon}
        size={isMobile ? 16 : 24}
        color={palette.grey.primary}
      />
      <Box>
        <Typography
          variant={isMobile ? "textRegular" : "actionBigIcon"}
          color={palette.grey.primary}
          sx={textSx}
        >
          {text}
        </Typography>
        {subtext && (
          <Typography color={palette.grey.primary}>{subtext}</Typography>
        )}
      </Box>
    </Box>
  );
};

export default memo(GradientButton);
